import React, { useState } from "react";
import {
  Image,
  Text,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import Separator from "../Separator";
import { AppImages } from "../../utility/AppImages";
import { CommonButton } from "../../common/CommonButton";
import ProfileTooltip from "./ProfileTooltip";
import UserBalanceSkeleton from "../skeleton-components/UserBalanceSkeleton";
import TokenWalkthrough from "../walk-through-common-components/profileTooltips/TokenWalkthrough";
import CoinWalkthrough from "../walk-through-common-components/profileTooltips/CoinWalkthrough";

interface UserProfileData {
  tokens?: number;
  coins?: number;
}

interface UserBalanceProps {
  jumpToPurchaseToken: (value: boolean) => void;
  userProfileData?: UserProfileData;
  totalShowInfo: boolean;
  setTotalShowInfo: (value: boolean) => void;
  coinsShowInfo: boolean;
  setCoinsShowInfo: (value: boolean) => void;
  accessToken?: string;
  isLoading?: boolean;
  setActiveTab?: any;
  scrollViewRef?: any;
  totalBalanceShowInfo?: boolean;
  availableCreditsShowInfo?: boolean;
}

const UserBalance: React.FC<UserBalanceProps> = ({
  jumpToPurchaseToken,
  userProfileData,
  totalShowInfo,
  setTotalShowInfo,
  coinsShowInfo,
  setCoinsShowInfo,
  accessToken,
  isLoading = false,
  setActiveTab,
  scrollViewRef,
  totalBalanceShowInfo = false,
  availableCreditsShowInfo = false,
}) => {
  const deviceSize = useDeviceSize();
  const styles = useStyles();

  if (isLoading) {
    return <UserBalanceSkeleton />;
  }

  return (
    <View
      style={{
        gap:
          deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
            ? 12
            : 30,
        flex:
          deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
            ? 1
            : 0.5,
        width:
          deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
            ? "99%"
            : "auto",
        margin:
          deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
            ? 1
            : 0,
      }}
    >
      <TokenWalkthrough
        content={
          <LinearGradient
            colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              padding: 24,
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 12,
              alignSelf: "stretch",
              borderRadius: 8,
              borderWidth: 0.8,
              borderColor: AppColors.gray_374151,
              ...(coinsShowInfo || totalBalanceShowInfo || availableCreditsShowInfo ? { zIndex: -1 } : {}),
            }}
          >
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                alignSelf: "stretch",
              }}
            >
              <Text
                style={[
                  deviceSize === DEVICE_SIZES.XS ||
                  deviceSize === DEVICE_SIZES.SM
                    ? AppFontStyle.BOLD_ANYBODY_18
                    : AppFontStyle.BOLD_ANYBODY_24,
                  { lineHeight: 27.72 },
                ]}
              >
                Token Balance
              </Text>
              <TouchableOpacity
                onPress={() => setTotalShowInfo(!totalShowInfo)}
                style={{ flexDirection: "row", alignItems: "center", gap: 4 }}
              >
                <Text
                  style={[
                    AppFontStyle.BOLD_ANYBODY_16,
                    {
                      color: AppColors.koBrandRed,
                      lineHeight: 18.48,
                      marginRight: 4,
                    },
                  ]}
                >
                  What are tokens?
                </Text>
                <Image
                  source={AppImages.icInfoCircle}
                  style={{ height: 20, width: 20, resizeMode: "contain" }}
                />
              </TouchableOpacity>
            </View>

            <Separator
              color={AppColors.gray_374151}
              height={1}
              marginTop={0}
              marginBottom={0}
              marginHorizontal={0}
            />

            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                alignSelf: "stretch",
              }}
            >
              <View
                style={{ flexDirection: "row", gap: 12, alignItems: "center" }}
              >
                <Image
                  source={AppImages.Ko_Token}
                  style={{
                    height:
                      deviceSize === DEVICE_SIZES.XS ||
                      deviceSize === DEVICE_SIZES.SM
                        ? 40
                        : 51,
                    width:
                      deviceSize === DEVICE_SIZES.XS ||
                      deviceSize === DEVICE_SIZES.SM
                        ? 44
                        : 56,
                    resizeMode: "contain",
                  }}
                />
                <Text
                  style={
                    deviceSize === DEVICE_SIZES.XS ||
                    deviceSize === DEVICE_SIZES.SM
                      ? AppFontStyle.REGULAR_ANYBODY_22
                      : AppFontStyle.REGULAR_BEBAS_NEUE_35
                  }
                >
                  {userProfileData?.tokens || userProfileData?.tokens === 0
                    ? userProfileData?.tokens
                    : "-"}
                </Text>
              </View>
              <CommonButton
                buttonStyle={styles.buyBeltButtonStyle}
                textStyle={[AppFontStyle.BOLD_ANYBODY_12, {}]}
                title="Buy Tokens"
                onTap={() => {
                  jumpToPurchaseToken(true);
                }}
              />
            </View>

            {/* Exchange from Blitz Section */}
            <View
              style={{
                padding: 16,
                flexDirection: "column",
                alignItems: "center",
                gap: 12,
                alignSelf: "stretch",
                borderRadius: 8,
                borderWidth: 0.8,
                borderColor: AppColors.gray_374151,
              }}
            >
              <Text
                style={[
                  AppFontStyle.MEDIUM_ANYBODY_16,
                  { lineHeight: 18.48, alignSelf: "flex-start" },
                ]}
              >
                Exchange from Blitz
              </Text>
              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_14,
                  {
                    color: AppColors.gray_9CA3AF,
                    lineHeight: 16.17,
                    alignSelf: "flex-start",
                  },
                ]}
              >
                Convert your KO Blitz tokens into KO Platform tokens.
              </Text>
              <CommonButton
                buttonStyle={styles.exchangeButtonStyle}
                textStyle={[
                  AppFontStyle.BOLD_ANYBODY_12,
                  { color: AppColors.koBrandRed },
                ]}
                title="Exchange Now"
                onTap={() => {
                  // Add exchange functionality here
                }}
              />
            </View>

            {totalShowInfo && (
              <TouchableWithoutFeedback onPress={(e) => e.stopPropagation()}>
                <View
                  style={[
                    styles.infoPopupContainer,
                    {
                      right:
                        deviceSize === DEVICE_SIZES.XS ||
                        deviceSize === DEVICE_SIZES.SM
                          ? 0
                          : 30,
                    },
                    {
                      top:
                        deviceSize === DEVICE_SIZES.XS ||
                        deviceSize === DEVICE_SIZES.SM
                          ? 50
                          : -10,
                    },
                  ]}
                >
                  <ProfileTooltip
                    content={
                      <View>
                        <Text style={AppFontStyle.MEDIUM_ANYBODY_16}>
                          What are KO Tokens?
                        </Text>
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { marginTop: 8 },
                          ]}
                        >
                          KO Tokens are your in-platform power – used to join
                          events, buy belts, and send gifts across Kall Out KO.
                        </Text>
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { marginTop: 12 },
                          ]}
                        >
                          You can get tokens by purchasing them or earning them
                          through wins and engagement.
                        </Text>
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { marginTop: 4 },
                          ]}
                        >
                          Each token holds real in-platform value and fuels
                          every challenge you take on.
                        </Text>
                        <Text
                          style={[
                            AppFontStyle.MEDIUM_ANYBODY_16,
                            { marginTop: 16 },
                          ]}
                        >
                          💷 Value:
                        </Text>
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { marginTop: 8 },
                          ]}
                        >
                          1 KO Token = £0.010
                        </Text>
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { marginTop: 12 },
                          ]}
                        >
                          In short:
                        </Text>
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { marginTop: 4 },
                          ]}
                        >
                          Tokens power your play.
                        </Text>
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { marginTop: 4 },
                          ]}
                        >
                          Get them. Use them. Rise through the ranks.
                        </Text>
                      </View>
                    }
                    bottom={
                      deviceSize === DEVICE_SIZES.XS ||
                      deviceSize === DEVICE_SIZES.SM ||
                      deviceSize === DEVICE_SIZES.MD ||
                      deviceSize === DEVICE_SIZES.LG
                        ? -270
                        : -50
                    }
                    right={
                      deviceSize === DEVICE_SIZES.XS ||
                      deviceSize === DEVICE_SIZES.SM ||
                      deviceSize === DEVICE_SIZES.MD ||
                      deviceSize === DEVICE_SIZES.LG
                        ? 0
                        : 20
                    }
                    width={320}
                    visible={totalShowInfo}
                    setVisible={setTotalShowInfo}
                  />
                </View>
              </TouchableWithoutFeedback>
            )}
          </LinearGradient>
        }
      />
      <CoinWalkthrough
        setActiveTab={setActiveTab}
        scrollViewRef={scrollViewRef}
        content={
          <LinearGradient
            colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              padding: 24,
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 12,
              alignSelf: "stretch",
              borderRadius: 8,
              borderWidth: 0.8,
              borderColor: AppColors.gray_374151,
              ...(totalShowInfo || totalBalanceShowInfo || availableCreditsShowInfo ? { zIndex: -1 } : {}),
            }}
          >
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                alignSelf: "stretch",
              }}
            >
              <Text
                style={[
                  deviceSize === DEVICE_SIZES.XS ||
                  deviceSize === DEVICE_SIZES.SM
                    ? AppFontStyle.BOLD_ANYBODY_18
                    : AppFontStyle.BOLD_ANYBODY_24,
                  { lineHeight: 27.72 },
                ]}
              >
                Coin Balance
              </Text>
              <TouchableOpacity
                onPress={() => setCoinsShowInfo(!coinsShowInfo)}
                style={{ flexDirection: "row", alignItems: "center", gap: 4 }}
              >
                <Text
                  style={[
                    AppFontStyle.BOLD_ANYBODY_16,
                    {
                      color: AppColors.koBrandRed,
                      lineHeight: 18.48,
                      marginRight: 4,
                    },
                  ]}
                >
                  How to earn coins?
                </Text>
                <Image
                  source={AppImages.icInfoCircle}
                  style={{ height: 20, width: 20, resizeMode: "contain" }}
                />
              </TouchableOpacity>
            </View>

            <Separator
              color={AppColors.gray_374151}
              height={1}
              marginTop={0}
              marginBottom={0}
              marginHorizontal={0}
            />

            <View
              style={{
                flexDirection: "row",
                gap: 12,
                alignItems: "center",
              }}
            >
              <Image
                source={AppImages.Ko_Coin}
                style={{
                  height:
                    deviceSize === DEVICE_SIZES.XS ||
                    deviceSize === DEVICE_SIZES.SM
                      ? 40
                      : 51,
                  width:
                    deviceSize === DEVICE_SIZES.XS ||
                    deviceSize === DEVICE_SIZES.SM
                      ? 44
                      : 56,
                  resizeMode: "contain",
                }}
              />
              <Text
                style={
                  deviceSize === DEVICE_SIZES.XS ||
                  deviceSize === DEVICE_SIZES.SM
                    ? AppFontStyle.REGULAR_ANYBODY_22
                    : AppFontStyle.REGULAR_BEBAS_NEUE_35
                }
              >
                {userProfileData?.coins || userProfileData?.coins === 0
                  ? userProfileData?.coins
                  : "-"}
              </Text>
            </View>
            {coinsShowInfo && (
              <TouchableWithoutFeedback onPress={(e) => e.stopPropagation()}>
                <View
                  style={[
                    styles.infoPopupContainer,
                    {
                      right:
                        deviceSize === DEVICE_SIZES.XS ||
                        deviceSize === DEVICE_SIZES.SM
                          ? 0
                          : 30,
                    },
                  ]}
                >
                  <ProfileTooltip
                    content={
                      <View>
                        <Text style={AppFontStyle.MEDIUM_ANYBODY_16}>
                          What are 💰 KO Coins?
                        </Text>
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { marginTop: 8 },
                          ]}
                        >
                          stay active. stack coins.
                        </Text>
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { marginTop: 4 },
                          ]}
                        >
                          KO Coins are your engagement rewards – earned just by
                          showing up, cheering, sharing, and staying part of the
                          action.
                        </Text>
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { marginTop: 12 },
                          ]}
                        >
                          You can't spend coins, but you can convert them once
                          you've built up enough.
                        </Text>
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { marginTop: 4 },
                          ]}
                        >
                          That's how you turn your presence into power.
                        </Text>
                        <Text
                          style={[
                            AppFontStyle.MEDIUM_ANYBODY_16,
                            { marginTop: 16 },
                          ]}
                        >
                          🔥 Conversion:
                        </Text>
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { marginTop: 8 },
                          ]}
                        >
                          10,000 KO Coins = £5 worth of Tokens
                        </Text>
                        <Text
                          style={[
                            AppFontStyle.REGULAR_ANYBODY_14,
                            { marginTop: 12 },
                          ]}
                        >
                          Simple. Earn. Convert. Compete.
                        </Text>
                      </View>
                    }
                    bottom={
                      deviceSize === DEVICE_SIZES.XS ||
                      deviceSize === DEVICE_SIZES.SM ||
                      deviceSize === DEVICE_SIZES.MD ||
                      deviceSize === DEVICE_SIZES.LG
                        ? -142
                        : -85
                    }
                    right={
                      deviceSize === DEVICE_SIZES.XS ||
                      deviceSize === DEVICE_SIZES.SM ||
                      deviceSize === DEVICE_SIZES.MD ||
                      deviceSize === DEVICE_SIZES.LG
                        ? 0
                        : 20
                    }
                    width={320}
                    visible={coinsShowInfo}
                    setVisible={setCoinsShowInfo}
                  />
                </View>
              </TouchableWithoutFeedback>
            )}
          </LinearGradient>
        }
      />
    </View>
  );
};

export default UserBalance;

const useStyles = CreateResponsiveStyle(
  {
    buyBeltButtonStyle: {
      paddingVertical: 12,
      shadowOpacity: 0,
      paddingHorizontal: 16,
      borderRadius: 10,
      borderWidth: 1,
      backgroundColor: AppColors.appViolentColor_6D08C3,
      borderColor: AppColors.appViolentColor_6D08C3,
    },
    exchangeButtonStyle: {
      paddingVertical: 12,
      shadowOpacity: 0,
      paddingHorizontal: 16,
      borderRadius: 10,
      borderWidth: 1,
      backgroundColor: "transparent",
      borderColor: AppColors.koBrandRed,
    },
    infoPopupContainer: {
      position: "absolute",
      top: -10,
      zIndex: 9999,
    },
  },
  {
    [DEVICE_SIZES.MD]: {},
    [DEVICE_SIZES.LG]: {},
    [DEVICE_SIZES.SM]: {},
    [DEVICE_SIZES.XS]: {},
  }
);
